import { UserProfile } from '../types';

/**
 * Formats a name to "Last Name, First Name" format.
 * Examples:
 * - "Bella Bailey" -> "Bailey, Bella"
 * - "Bailey, Bella" -> "Bailey, Bella"
 * - profile with contactLastName="Bailey", contactFirstName="Bella" -> "Bailey, Bella"
 */
export function formatLastNameFirst(
  name?: string | null,
  profile?: Partial<UserProfile> | null
): string {
  if (profile?.contactLastName && profile?.contactFirstName) {
    return `${profile.contactLastName}, ${profile.contactFirstName}`;
  }

  const rawName = name || profile?.name || '';
  if (!rawName || rawName.trim() === '') {
    return '';
  }

  const trimmed = rawName.trim();

  // If already formatted like "Last, First", return as is
  if (trimmed.includes(',')) {
    return trimmed;
  }

  // Handle single-word names or special placeholders
  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) {
    return trimmed;
  }

  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, parts.length - 1).join(' ');
  return `${lastName}, ${firstName}`;
}

/**
 * Gets a sort key for ordering students by last name, then first name.
 */
export function getStudentLastNameSortKey(
  name?: string | null,
  profile?: Partial<UserProfile> | null
): string {
  if (profile?.contactLastName) {
    const first = profile.contactFirstName || '';
    return `${profile.contactLastName.toLowerCase()}, ${first.toLowerCase()}`;
  }

  const rawName = name || profile?.name || '';
  if (!rawName) return 'zzz';

  const trimmed = rawName.trim();
  if (trimmed.includes(',')) {
    return trimmed.toLowerCase();
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) {
    return trimmed.toLowerCase();
  }

  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, parts.length - 1).join(' ');
  return `${lastName.toLowerCase()}, ${firstName.toLowerCase()}`;
}

/**
 * Sorts an array of student/applicant records alphabetically by last name.
 */
export function sortStudentsByLastName<T>(
  items: T[],
  getName: (item: T) => string | undefined | null,
  getProfile?: (item: T) => Partial<UserProfile> | undefined | null
): T[] {
  return [...items].sort((a, b) => {
    const keyA = getStudentLastNameSortKey(getName(a), getProfile ? getProfile(a) : null);
    const keyB = getStudentLastNameSortKey(getName(b), getProfile ? getProfile(b) : null);
    return keyA.localeCompare(keyB);
  });
}

/**
 * Checks if an email address belongs to a prohibited domain.
 * Supports patterns like "k12.sc.us", "@k12.sc.us", "*.k12.sc.us", "district.edu".
 */
export function isEmailDomainProhibited(
  email?: string | null,
  prohibitedDomains?: string[] | null
): { isProhibited: boolean; matchedDomain?: string } {
  if (!email || !prohibitedDomains || prohibitedDomains.length === 0) {
    return { isProhibited: false };
  }
  const cleanEmail = email.trim().toLowerCase();
  const parts = cleanEmail.split('@');
  if (parts.length < 2) return { isProhibited: false };
  const emailDomain = parts[parts.length - 1].trim();

  for (const rawPattern of prohibitedDomains) {
    if (!rawPattern) continue;
    let pattern = rawPattern.trim().toLowerCase();
    // Remove leading @ or *. or .
    if (pattern.startsWith('@')) pattern = pattern.slice(1);
    if (pattern.startsWith('*.')) pattern = pattern.slice(2);
    if (pattern.startsWith('.')) pattern = pattern.slice(1);
    pattern = pattern.trim();
    if (!pattern) continue;

    // Direct match: e.g. "spart6.org" matches "spart6.org"
    if (emailDomain === pattern) {
      return { isProhibited: true, matchedDomain: rawPattern };
    }
    // Subdomain match: e.g. "k12.sc.us" matches "greenville.k12.sc.us"
    if (emailDomain.endsWith('.' + pattern)) {
      return { isProhibited: true, matchedDomain: rawPattern };
    }
  }

  return { isProhibited: false };
}

